import { Money } from "@redotech/money";
import { useHandler } from "@redotech/react-util/hook";
import { Lazy } from "@redotech/react-util/init";
import {
  fullCoverage,
  getHtml,
  getReturnType,
  texts,
} from "@redotech/redo-model/checkbox";
import { Coverage, PaymentModel } from "@redotech/redo-model/coverage";
import { ToggleFields } from "@redotech/redo-model/team";
import { CURRENCY_FORMAT } from "@redotech/redo-web/currency";
import Logo from "@redotech/redo-web/logo.svg";
import htmlReactParser from "html-react-parser";
import { ChangeEvent, ReactNode, Suspense, lazy, memo, useState } from "react";
import * as toggle from "./toggle.module.css";

const RedoModal = lazy(async () => {
  const { RedoModal } = await import("./modal");
  return { default: RedoModal };
});

const SingleToggleLabel = ({
  children,
  branding,
  color,
  fontSize,
  coverage,
  price,
  toggleText,
  toggleTextAfterRedo,
}: {
  children: ReactNode;
  branding: boolean;
  color: string;
  fontSize?: number;
  coverage: Coverage;
  price?: string;
  toggleText?: string | null;
  toggleTextAfterRedo?: string | null;
}) => {
  const name: string =
    texts[`${+coverage.exchange}${+coverage.refund}${+coverage.storeCredit}`];
  return (
    <span
      data-target="toggle-label"
      className={toggle.label}
      style={{
        color,
        fontSize: fontSize ? `${fontSize.toString()}px` : "12px",
      }}
    >
      {toggleText ? (
        htmlReactParser(toggleText)
      ) : (
        <>
          {children} {name !== fullCoverage && <>for {name} </>}
        </>
      )}
      {price && ` for ${price}`}
      {branding && (
        <>
          {" "}
          via{" "}
          <div className={toggle.redoLogo} data-target="toggle-logo">
            <Logo style={fontSize ? { height: `${fontSize}px` } : undefined} />
          </div>{" "}
          {toggleTextAfterRedo}
        </>
      )}
    </span>
  );
};

export const TwoLineToggleLabel = ({
  children,
  color,
  fontSize,
  coverage,
  price,
  toggleTitleText,
  toggleSubtitleText,
}: {
  children: ReactNode;
  color: string;
  fontSize?: number;
  coverage: Coverage;
  price?: string;
  toggleTitleText: string;
  toggleSubtitleText: string;
}) => {
  const name: string =
    texts[`${+coverage.exchange}${+coverage.refund}${+coverage.storeCredit}`];
  return (
    <div className={toggle.doubleTextContainer}>
      <span
        data-target="toggle-label"
        className={toggle.label}
        style={{
          color,
          fontSize: fontSize ? `${fontSize.toString()}px` : "12px",
        }}
      >
        {toggleTitleText ? (
          htmlReactParser(toggleTitleText)
        ) : (
          <>
            {children} {name !== fullCoverage && <>for {name} </>}
          </>
        )}
        {price && ` for ${price}`}
      </span>
      <br></br>
      <span
        className={toggle.subtext}
        style={{ color }}
        data-target="toggle-subtext"
      >
        {htmlReactParser(toggleSubtitleText)}
      </span>
    </div>
  );
};

const Toggle = memo(
  ({
    color,
    value,
    onValue,
  }: {
    color: string;
    value: boolean;
    onValue?: (value: boolean) => void;
  }) => {
    const onChange = useHandler((e: ChangeEvent) => {
      onValue?.((e.target as HTMLInputElement).checked);
    });

    return (
      <div style={{ "--redo-color": color } as any}>
        <label
          className={toggle.toggleContainer}
          data-target="toggle-container"
        >
          <input
            className={toggle.checkbox}
            type="checkbox"
            checked={value}
            onChange={onChange}
            data-target="toggle-input"
          />
          <div className={toggle.switch0} data-target="toggle-switch">
            <div className={toggle.circle} data-target="toggle-circle" />
          </div>
        </label>
      </div>
    );
  },
);

export const ToggleImage = memo(({ img_url }: { img_url: string }) => {
  return (
    <img
      className={toggle.imageContainer}
      src={img_url}
      data-target="deluxe-toggle-image"
    ></img>
  );
});

export const RedoToggle = ({
  branding,
  color,
  coverage,
  html,
  fontSize,
  toggleText,
  toggleTextAfterRedo,
  toggleFields,
  value,
  onChange,
  paymentModel,
  price,
  isDelux,
  customCss,
  foundSelector,
}: {
  branding: boolean;
  color: string;
  coverage: Coverage;
  html?: string | null;
  fontSize?: number;
  toggleText?: string | null;
  toggleTextAfterRedo?: string | null;
  toggleFields: ToggleFields;
  value: boolean;
  onChange?(value: boolean): void;
  paymentModel: PaymentModel;
  price: Money | undefined;
  isDelux: boolean;
  customCss?: string;
  foundSelector?: string;
}) => {
  const [modalOpen, setModalOpen] = useState(false);

  const handleAdd = useHandler(() => {
    onChange && onChange(true);
  }); // TODO(paul): would be nice to add to cart
  const onModalClose = useHandler(() => setModalOpen(false));

  const label = isDelux ? (
    <TwoLineToggleLabel
      color={color}
      fontSize={fontSize}
      coverage={coverage}
      price={
        paymentModel === "customer_paid" && price
          ? CURRENCY_FORMAT(price.currency).format(price.amount)
          : undefined
      }
      toggleTitleText={toggleFields.titleText}
      toggleSubtitleText={
        value ? toggleFields.positiveSubtext : toggleFields.negativeSubtext
      }
    >
      {html ? (
        htmlReactParser(getHtml(html, getReturnType(coverage)))
      ) : (
        <strong>Free Unlimited Return</strong>
      )}
    </TwoLineToggleLabel>
  ) : (
    <SingleToggleLabel
      branding={branding}
      color={color}
      fontSize={fontSize}
      coverage={coverage}
      price={
        paymentModel === "customer_paid" && price
          ? CURRENCY_FORMAT(price.currency).format(price.amount)
          : undefined
      }
      toggleText={toggleText}
      toggleTextAfterRedo={toggleTextAfterRedo}
    >
      {html ? (
        htmlReactParser(getHtml(html, getReturnType(coverage)))
      ) : (
        <strong>Free Unlimited Return</strong>
      )}
    </SingleToggleLabel>
  );

  let checkbox_: ReactNode;
  switch (paymentModel) {
    case "customer_paid":
      checkbox_ = (
        <Toggle color={color} value={value} onValue={onChange}></Toggle>
      );
      break;
    case "merchant_paid":
      checkbox_ = <span></span>;
      break;
  }

  return (
    <>
      {customCss ? <style>{customCss}</style> : null}
      <div
        className={toggle.container}
        data-target="toggle-root"
        found-selector={foundSelector}
        style={{ color }}
      >
        {label}
        {checkbox_}
      </div>
      <Suspense>
        <Lazy init={modalOpen}>
          <RedoModal
            open={modalOpen}
            onAdd={handleAdd}
            onClose={onModalClose}
          ></RedoModal>
        </Lazy>
      </Suspense>
    </>
  );
};

export const DeluxeToggleArray = ({
  image_url,
  foundSelector,
  children,
}: {
  image_url: string;
  foundSelector?: string;
  children: ReactNode;
}) => {
  return (
    <>
      <div
        className={toggle.deluxeContainerAll}
        data-target="deluxe-toggle-root"
        found-selector={foundSelector}
      >
        <ToggleImage img_url={image_url}></ToggleImage>
        <div
          className={toggle.deluxeContainerTextToggle}
          data-target="deluxe-toggle-container"
        >
          {children}
        </div>
      </div>
    </>
  );
};
